import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography,Chip, Checkbox, Table, TableHead, TableBody, TableRow, TableCell, Avatar, TableContainer, Paper, Box
} from '@mui/material';
import ConfirmationDialog from './ConfirmationDialog.comp';
import { LEARNING_PROGRAM_STATUS } from '../../../utils/constants';

const LinkProgramDialog = ({programs, open, onSubmit, loading, onClose, orgName, orgCode, linkedProgramCodes = [], linkingLoading = false, targetNoun = 'client',}) => {
    const [selectedCodes, setSelectedCodes] = useState([]);
    const [showFirstConfirmation, setShowFirstConfirmation] = useState(false);
    const [showSecondConfirmation, setShowSecondConfirmation] = useState(false);

    useEffect(() => {
        if (open) {
            setSelectedCodes([]);
            setShowFirstConfirmation(false);
            setShowSecondConfirmation(false);
        }
    }, [open]);

    const handleToggle = (code) => {
        setSelectedCodes(prev =>
            prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
        );
    };

    const handleSubmit = () => {
        // Show first confirmation
        setShowFirstConfirmation(true);
    };

    const handleFirstConfirm = () => {
        setShowFirstConfirmation(false);
        // Show second confirmation
        setShowSecondConfirmation(true);
    };

    const handleSecondConfirm = () => {
        setShowSecondConfirmation(false);
        // Actually call the API
        onSubmit(orgCode, selectedCodes);
    };

    const handleCloseFirstConfirmation = () => {
        setShowFirstConfirmation(false);
    };

    const handleCloseSecondConfirmation = () => {
        setShowSecondConfirmation(false);
    };

    const linked = programs.filter(a => linkedProgramCodes.includes(a.code));
    const unlinked = programs.filter(a => !linkedProgramCodes.includes(a.code));

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="md" 
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '24px',
                    overflow: 'hidden',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                }
            }}
            BackdropProps={{
                style: { backgroundColor: 'rgba(0, 0, 0, 0.4)' }
            }}
        >
            <DialogTitle sx={{ color: '#111', fontSize: '22px', fontWeight: 800, textAlign: 'center', pt: 4, pb: 0 }}>
                Link Learning Programs
            </DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'center', pb: 1 }}>
                <Box sx={{
                    display: 'inline-block', backgroundColor: 'rgba(255, 105, 0, 0.08)', color: '#F15A24',
                    fontSize: '13px', fontWeight: 700, px: 1.5, py: 0.4, borderRadius: '20px',
                }}>
                    {orgName}
                </Box>
            </Box>
            <DialogContent sx={{ px: 4 }}>
                {linkingLoading  ? (
                    <Typography>Loading programs...</Typography>
                ) : (
                    <>
                        <Typography sx={{ textAlign: 'center', fontSize: '15px', color: '#666', mb: 3 }}>
                            Select the learning program(s) that you would like to link to this {targetNoun}.
                        </Typography>
                        <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #eee', borderRadius: '16px' }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 800, color: '#444', borderBottom: '2px solid #eee', backgroundColor: '#f8f9fa' }}>Program</TableCell>
                                        <TableCell sx={{ fontWeight: 800, color: '#444', borderBottom: '2px solid #eee', backgroundColor: '#f8f9fa' }}>Sessions</TableCell>
                                        <TableCell sx={{ fontWeight: 800, color: '#444', borderBottom: '2px solid #eee', backgroundColor: '#f8f9fa' }}>Time (mins)</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 800, color: '#444', borderBottom: '2px solid #eee', backgroundColor: '#f8f9fa' }}>Select</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {[...linked, ...unlinked].map((program) => {
                                        const alreadyLinked = linkedProgramCodes.includes(program.code);
                                        const notReady = !program.is_ready;
                                        const notPublished = !alreadyLinked && !notReady && Number(program.status) !== LEARNING_PROGRAM_STATUS.PUBLISHED;

                                        return (
                                            <TableRow
                                                key={program.code || program.id}
                                                sx={{
                                                backgroundColor: alreadyLinked ? 'rgba(0, 151, 178, 0.05)' : (notReady || notPublished) ? '#fafafa' : 'inherit'
                                                }}
                                            >
                                                <TableCell sx={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #eee' }}>
                                                    <Avatar
                                                        src={program.image_url}
                                                        variant="rounded"
                                                        sx={{ width: 56, height: 56, border: '1px solid #eee', borderRadius: '12px' }}
                                                    />
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Typography
                                                            sx={{
                                                                fontWeight: 500,
                                                                lineHeight: '1.5',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                marginLeft:'15px',
                                                            }}
                                                        >
                                                            {program.full_name}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid #eee', color: '#555' }}>{program.no_of_sessions}</TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid #eee', color: '#555' }}>{program.time_to_complete_minutes}</TableCell>
                                                <TableCell align="center" sx={{ borderBottom: '1px solid #eee' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    {alreadyLinked ? (
                                                        <Chip
                                                            label="Already Linked"
                                                            size="small"
                                                            sx={{
                                                                backgroundColor:'rgba(0, 151, 178, 0.1)',
                                                                color: '#0097b2',
                                                                fontWeight: 'bold',
                                                                borderRadius: '8px'
                                                            }}
                                                        />
                                                    ) : notReady ? (
                                                        <Chip
                                                            label="Not Ready"
                                                            size="small"
                                                            sx={{
                                                                backgroundColor:'transparent',
                                                                color: '#D32F2F',
                                                                fontWeight: 'bold',
                                                                border: '1px solid #f5c6c6'
                                                            }}
                                                        />
                                                    ) : notPublished ? (
                                                        <Chip
                                                            label="Not Published"
                                                            size="small"
                                                            title="Publish the program before linking it to a client org."
                                                            sx={{
                                                                backgroundColor:'transparent',
                                                                color: '#9E7B00',
                                                                fontWeight: 'bold',
                                                                border: '1px solid #f0e0a8'
                                                            }}
                                                        />
                                                    ) : (
                                                        <Checkbox
                                                            checked={selectedCodes.includes(program.code)}
                                                            onChange={() => handleToggle(program.code)}
                                                            sx={{
                                                                color: '#F15A24',
                                                                '&.Mui-checked': { color: '#F15A24' }
                                                            }}
                                                            disabled={alreadyLinked || notReady || notPublished}
                                                        />
                                                    )}
                                                </Box>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ pt: 3, pb: 4 }}>
                <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                    <Button
                        onClick={onClose}
                        disabled={loading}
                        sx={{
                            minWidth: 180,
                            px: 4,
                            py: 1.25,
                            borderRadius: '10px',
                            backgroundColor: '#fff',
                            color: '#111',
                            fontWeight: 700,
                            fontSize: '16px',
                            textTransform: 'none',
                            border: '1.5px solid #111',
                            '&:hover': { backgroundColor: '#f7f7f7', borderColor: '#111' },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={loading || selectedCodes.length === 0}
                        sx={{
                            minWidth: 180,
                            px: 4,
                            py: 1.25,
                            borderRadius: '10px',
                            backgroundColor: '#FF6900',
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: '16px',
                            textTransform: 'none',
                            boxShadow: 'none',
                            '&:hover': { backgroundColor: '#e55e00', boxShadow: 'none' },
                            '&.Mui-disabled': { backgroundColor: '#FFD0AA', color: '#fff' },
                        }}
                    >
                        {loading ? 'Linking...' : `Link (${selectedCodes.length})`}
                    </Button>
                </Box>
            </DialogActions>

            {/* First Confirmation Dialog */}
            <ConfirmationDialog
                open={showFirstConfirmation}
                onClose={handleCloseFirstConfirmation}
                onConfirm={handleFirstConfirm}
                title="Confirm linking"
                message={`Do you want to link <b>${selectedCodes.length}</b> program(s) to <b>${orgName}?</b>`}
                confirmText="Proceed"
                cancelText="Cancel"
            />

            {/* Second Confirmation Dialog */}
            <ConfirmationDialog
                open={showSecondConfirmation}
                onClose={handleCloseSecondConfirmation}
                onConfirm={handleSecondConfirm}
                title="Are You Sure?"
                message="Are you sure? Unlinking will not be possible once linked."
                confirmText="Yes, Link Now"
                cancelText="Cancel"
                isWarning={true}
            />
        </Dialog>
    );
};

export default LinkProgramDialog;